import React, { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_META = {
  approved: {
    label: "Verified",
    color: "success",
    icon: <VerifiedUserIcon sx={{ fontSize: 18, mr: 0.5 }} />,
    message: "Your identity has been verified. A Verified badge will appear on your profile.",
    severity: "success",
  },
  in_review: {
    label: "Under review",
    color: "warning",
    icon: <HourglassTopIcon sx={{ fontSize: 18, mr: 0.5 }} />,
    message: "Your verification was submitted and is being reviewed. The Verified badge is added only after approval. You will be notified when a decision has been made.",
    severity: "info",
  },
  in_progress: {
    label: "In progress",
    color: "info",
    icon: <HourglassTopIcon sx={{ fontSize: 18, mr: 0.5 }} />,
    message: "Your verification is in progress. If you closed the window, you can continue below.",
    severity: "info",
  },
  not_started: {
    label: "Not started",
    color: "default",
    icon: null,
    message: null,
    severity: null,
  },
  declined: {
    label: "Declined",
    color: "error",
    icon: <ErrorOutlineIcon sx={{ fontSize: 18, mr: 0.5 }} />,
    message: "Your verification was declined. You may try again if you believe this is a mistake.",
    severity: "error",
  },
  abandoned: {
    label: "Incomplete",
    color: "warning",
    icon: null,
    message: "Your previous verification was not completed. You can start a new one.",
    severity: "warning",
  },
  expired: {
    label: "Expired",
    color: "default",
    icon: null,
    message: "Your previous verification session expired. You can start a new one.",
    severity: "warning",
  },
};

function getCtaLabel(status) {
  if (!status || status === "not_started") return "Start identity verification";
  if (status === "in_progress") return "Continue verification";
  if (status === "declined" || status === "abandoned" || status === "expired") return "Retry verification";
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function KycVerificationCard() {
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${basePath}/api/v1/account/kyc/status`, {
        credentials: "include",
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.ok) {
        setKycStatus(data.kyc?.status || null);
      } else {
        setKycStatus(null);
      }
    } catch (_) {
      setKycStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  async function handleStartVerification() {
    setStarting(true);
    setError(null);
    try {
      const res = await fetch(`${basePath}/api/v1/account/kyc/didit/session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.ok && data?.verificationUrl) {
        window.location.href = data.verificationUrl;
        return;
      }
      setError(data?.message || "Could not start verification. Please try again.");
    } catch (_) {
      setError("Could not start verification. Please check your connection and try again.");
    } finally {
      setStarting(false);
    }
  }

  const meta = STATUS_META[kycStatus] || null;
  const ctaLabel = getCtaLabel(kycStatus);
  const isApproved = kycStatus === "approved";

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: isApproved ? "success.main" : "rgba(71, 122, 181, 0.5)",
        borderRadius: 2,
        p: 2.5,
        mt: 1,
        mb: 1,
        position: "relative",
      }}
    >
      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Identity Verification
          </Typography>
          <Chip
            size="small"
            component="a"
            href="https://didit.me"
            target="_blank"
            rel="noopener noreferrer"
            clickable
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1, color: "text.primary" }}>
                  Powered by
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    fontSize: 12,
                    lineHeight: 1,
                    fontWeight: 700,
                    letterSpacing: 0.1,
                    color: "text.primary",
                  }}
                >
                  Didit
                </Typography>
              </Box>
            }
            sx={{
              height: 22,
              borderRadius: 1.5,
              bgcolor: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.35)",
              "& .MuiChip-label": { px: 1 },
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.28)",
                border: "1px solid rgba(255,255,255,0.5)",
              },
            }}
          />
        </Box>
        {loading ? (
          <CircularProgress size={16} />
        ) : meta ? (
          <Chip
            size="small"
            icon={meta.icon}
            label={meta.label}
            color={meta.color}
            sx={{ fontWeight: 500, fontSize: 12 }}
          />
        ) : null}
      </Box>

      {/* Explanation */}
      {!isApproved && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Optional but recommended. Once your verification is{" "}
          <strong>approved</strong>, you&apos;ll earn a{" "}
          <strong>Verified badge</strong> on your profile and increase trust in matchmaking.
        </Typography>
      )}

      {/* Status message */}
      {!loading && meta?.message && (
        <Alert severity={meta.severity} sx={{ mb: 1.5 }}>
          {meta.message}
        </Alert>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {/* CTA */}
      {!loading && ctaLabel && (
        <Button
          variant="contained"
          size="small"
          onClick={handleStartVerification}
          disabled={starting}
          sx={{ textTransform: "none", minWidth: 200 }}
        >
          {starting ? (
            <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
          ) : null}
          {starting ? "Starting…" : ctaLabel}
        </Button>
      )}

      {isApproved && !loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <VerifiedUserIcon color="success" />
          <Typography variant="body2" color="success.main" fontWeight={500}>
            Your identity is verified
          </Typography>
        </Box>
      )}

    </Box>
  );
}
